/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { SNMPDevice } from "@/api/snmp";
import { useSNMPDevice } from "@/hooks/useSNMPDevice";
import { formatBytes } from "@/utils/formatBytes";
import { Panel, Tag } from "@/components/servers/kit";
import { SkeletonRows } from "@/components/common/States";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { cn } from "@/lib/utils";

interface SNMPOverviewTabProps {
  device: SNMPDevice;
}

const OPER_STATUS_UP = 1;

export const SNMPOverviewTab: React.FC<SNMPOverviewTabProps> = ({ device }) => {
  const { interfaces = [], isLoadingInterfaces } = useSNMPDevice({ device });

  const upCount = interfaces.filter((i) => i.latest?.operStatus === OPER_STATUS_UP).length;
  const totalIn = interfaces.reduce((sum, i) => sum + (i.latest?.inOctets ?? 0), 0);
  const totalOut = interfaces.reduce((sum, i) => sum + (i.latest?.outOctets ?? 0), 0);

  const score = (s?: (typeof interfaces)[number]["latest"]) =>
    (s?.inErrors ?? 0) + (s?.outErrors ?? 0) + (s?.inDiscards ?? 0) + (s?.outDiscards ?? 0);
  const worstFirst = interfaces.filter((i) => score(i.latest) > 0).sort((a, b) => score(b.latest) - score(a.latest));

  if (isLoadingInterfaces) return <SkeletonRows rows={4} />;

  return (
    <div className="space-y-3">
      <div className="sw-sum">
        <div className={cn("sw-kpi", upCount === interfaces.length && interfaces.length > 0 && "sw-kpi--ok")}>
          <small>Ports up</small>
          <b>
            <AnimatedNumber value={upCount} mountFrom={null} />
            <i>{` / ${interfaces.length}`}</i>
          </b>
        </div>
        <div className="sw-kpi">
          <small>Total in</small>
          <b>{formatBytes(totalIn)}</b>
        </div>
        <div className="sw-kpi">
          <small>Total out</small>
          <b>{formatBytes(totalOut)}</b>
        </div>
        <div className={cn("sw-kpi", worstFirst.length > 0 ? "sw-kpi--warn" : "sw-kpi--ok")}>
          <small>Ports with errors</small>
          <b>
            <AnimatedNumber value={worstFirst.length} mountFrom={null} />
          </b>
        </div>
      </div>

      <Panel title="Ports with errors or discards" hint={worstFirst.length ? String(worstFirst.length) : undefined} className="sv-c12" defaultOpen>
        {worstFirst.length === 0 ? (
          <div className="sw-empty">No errors or discards on any port.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="sw-tbl">
              <thead>
                <tr>
                  <th>Port</th>
                  <th>In errors</th>
                  <th>Out errors</th>
                  <th>In discards</th>
                  <th>Out discards</th>
                </tr>
              </thead>
              <tbody>
                {worstFirst.map((iface) => (
                  <tr key={iface.id}>
                    <td>
                      {iface.ifAlias || iface.ifDescr || `Port ${iface.ifIndex}`}
                      {iface.latest?.operStatus !== OPER_STATUS_UP && (
                        <span style={{ marginLeft: 8 }}>
                          <Tag>down</Tag>
                        </span>
                      )}
                    </td>
                    <td>{iface.latest?.inErrors ?? 0}</td>
                    <td>{iface.latest?.outErrors ?? 0}</td>
                    <td>{iface.latest?.inDiscards ?? 0}</td>
                    <td>{iface.latest?.outDiscards ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
};
